import { pluralize } from "@inrev/common";
import { Option } from "@inrev/core";
import {
	categoryLabel,
	type ExperianCreditDetail,
	type ExperianPaymentMonth,
	type ExperianTradeline,
	formatMonthYear,
	formatReportDollars,
	latePaymentCount,
	paymentHistoryLabel,
	paymentHistoryRange,
	totalPastDue,
} from "@portal/api/account/admin/individualCreditReport";
import { cn } from "@portal/components/lib/utils";
import { Tag, type TagColor, Text } from "@portal/components/primitives";
import { DynamicDataTable } from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import type { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { DateTime } from "luxon";
import type { FC } from "react";
import { formatEmpty } from "../../../../../../../utils";
import { ReportSection, SectionSummary } from "./ReportSection";

type Detail = ExperianCreditDetail;
type Tradeline = ExperianTradeline;
type PaymentMonth = ExperianPaymentMonth;

const PAYMENT_MONTH_CLASSES: Record<PaymentMonth, string> = {
	on_time: "bg-chart-success-200",
	late_30: "bg-chart-amber-600",
	late_60: "bg-chart-orange-600",
	late_90_plus: "bg-chart-danger-700",
	derogatory: "bg-surface-primary",
	no_data: "bg-surface-neutral border border-bc-default",
};

const PAYMENT_LEGEND: { month: PaymentMonth; label: string }[] = [
	{ month: "on_time", label: "On time" },
	{ month: "late_30", label: "30 days late" },
	{ month: "late_60", label: "60 days" },
	{ month: "late_90_plus", label: "90+ days" },
	{ month: "derogatory", label: "Charge-off or collection" },
	{ month: "no_data", label: "No data" },
];

const STATUS_TAGS: Record<Tradeline["status"], { label: string; color: TagColor }> = {
	open: { label: "Open", color: "blue" },
	closed: { label: "Closed", color: "gray" },
	collection: { label: "Collection", color: "red" },
};

const accountDetail = (tradeline: Tradeline): string =>
	[
		tradeline.accountType ?? categoryLabel(tradeline.category),
		Option.isSome(tradeline.originalCreditor)
			? `orig. ${tradeline.originalCreditor}`
			: Option.map(tradeline.openedOn, (opened) => `opened ${formatMonthYear(opened)}`),
	]
		.filter(Option.isSome)
		.join(" · ");

const historyNote = (tradeline: Tradeline): string =>
	tradeline.status === "closed" && Option.isSome(tradeline.statusDate)
		? `Closed ${formatMonthYear(tradeline.statusDate)}`
		: "No payment history reported";

export const PaymentHistoryStrip: FC<{ tradeline: Tradeline }> = ({ tradeline }) => {
	if (tradeline.paymentHistory.length === 0) {
		return (
			<Text type="caption" color="tertiary">
				{historyNote(tradeline)}
			</Text>
		);
	}
	const range = paymentHistoryRange(tradeline);
	const from = range?.from.toFormat("MM/yyyy");
	const through = range?.through.toFormat("MM/yyyy");
	return (
		<div className="flex w-[210px] flex-col gap-1">
			<div
				className="flex w-full gap-0.5"
				role="img"
				aria-label={[
					paymentHistoryLabel(tradeline.paymentHistory),
					Option.isSome(range) ? `${from} to ${through}` : undefined,
				]
					.filter(Option.isSome)
					.join(", ")}
				data-testid="individual_credit_payment_history"
			>
				{tradeline.paymentHistory.map((month, index) => (
					<span
						key={index}
						className={cn("h-[18px] flex-1 rounded-[2px]", PAYMENT_MONTH_CLASSES[month])}
						data-month={month}
					/>
				))}
			</div>
			{Option.isSome(range) && (
				<div
					className="flex justify-between gap-2"
					aria-hidden
					data-testid="individual_credit_payment_history_range"
				>
					<Text type="caption" color="tertiary">
						{from}
					</Text>
					<Text type="caption" color="tertiary">
						{through}
					</Text>
				</div>
			)}
		</div>
	);
};

const columns: DynamicDataTableColumn<Tradeline>[] = [
	{
		id: "creditor",
		accessorKey: "creditor",
		header: "Account",
		size: 220,
		cell: ({ row }) => (
			<div className="flex min-w-0 flex-col gap-1.5">
				<Text type="body-strong" color="primary" truncate>
					{row.original.creditor}
				</Text>
				<Text type="caption" color="tertiary" truncate>
					{accountDetail(row.original)}
				</Text>
			</div>
		),
	},
	{
		id: "status",
		accessorKey: "status",
		header: "Status",
		size: 104,
		cell: ({ row }) => (
			<Tag color={STATUS_TAGS[row.original.status].color}>
				{STATUS_TAGS[row.original.status].label}
			</Tag>
		),
	},
	{
		id: "balance",
		accessorKey: "balance",
		header: "Balance",
		size: 100,
		cell: ({ row }) => formatEmpty(formatReportDollars(row.original.balance)),
	},
	{
		id: "limit",
		header: "Limit / orig.",
		size: 104,
		cell: ({ row }) => formatEmpty(formatReportDollars(row.original.limit?.amount)),
	},
	{
		id: "pastDue",
		accessorKey: "pastDue",
		header: "Past due",
		size: 88,
		cell: ({ row }) => formatEmpty(formatReportDollars(row.original.pastDue)),
	},
	{
		id: "monthlyPayment",
		accessorKey: "monthlyPayment",
		header: "Monthly",
		size: 88,
		cell: ({ row }) => formatEmpty(formatReportDollars(row.original.monthlyPayment)),
	},
	{
		id: "paymentHistory",
		header: "Payment history · 24 mo",
		size: 240,
		cell: ({ row }) => <PaymentHistoryStrip tradeline={row.original} />,
	},
];

export const TradelinesSection: FC<{ detail: Detail; pulledAt: string }> = ({
	detail,
	pulledAt,
}) => {
	const lates = latePaymentCount(detail.tradelines, DateTime.fromISO(pulledAt));
	return (
		<ReportSection
			title="Tradelines"
			testId="individual_credit_tradelines"
			summary={
				<SectionSummary>
					{formatEmpty(formatReportDollars(totalPastDue(detail.tradelines)))} past due ·{" "}
					{pluralize(lates, "late", "lates")} in 24 months
				</SectionSummary>
			}
		>
			<DynamicDataTable
				name="individual_credit_report_tradelines"
				columns={columns}
				data={detail.tradelines}
				expandToContent
			/>
			<div
				className="flex flex-wrap justify-end gap-x-4 gap-y-2"
				data-testid="individual_credit_payment_legend"
			>
				{PAYMENT_LEGEND.map(({ month, label }) => (
					<div key={month} className="flex items-center gap-1.5">
						<span
							aria-hidden
							className={cn("h-2.5 w-2.5 rounded-[2px]", PAYMENT_MONTH_CLASSES[month])}
						/>
						<Text type="caption" color="secondary">
							{label}
						</Text>
					</div>
				))}
			</div>
		</ReportSection>
	);
};
